import { ModalSize } from '@app/shared/components/modal/base-modal-enums';
import BaseModal from '@app/shared/components/modal/BaseModal';
import { FC } from 'react';
import { Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { useLocale } from '@app/core/hooks/useLocale';
import classNames from 'classnames';
import styles from './pwerm-2-waterfall-valuation-partial-table.module.scss';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { enteredPercentageViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';

interface TotalPvOfInterimCashFlowsModalProps {
  isOpen: boolean;
  onClose: () => void;
  getTransactionTypeLabel: (
    transaction: Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto
  ) => string;
  isTransactionCashPaidOrDividend: (
    transaction: Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto
  ) => boolean;
  transactions?: Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[] | null;
  tableTitle: string | undefined;
  totalPvValues: {
    title: string | undefined;
    value: number | undefined;
  };
}
export const TotalPvOfInterimCashFlowsModal: FC<TotalPvOfInterimCashFlowsModalProps> = ({
  isOpen,
  onClose,
  getTransactionTypeLabel,
  isTransactionCashPaidOrDividend,
  transactions,
  tableTitle,
  totalPvValues,
}) => {
  const { l } = useLocale();

  if (!transactions) {
    return null;
  }

  return (
    <BaseModal
      size={ModalSize.Large}
      isOpen={isOpen}
      onCloseButtonClick={() => {
        onClose();
      }}
      title={tableTitle}>
      <>
        <div className={styles['modal-content']}>
          <table
            className={classNames(
              'table-primary',
              'table-primary--sticky-first-column',
              styles['modal-table']
            )}>
            <thead>
              <tr>
                <th
                  className={classNames(
                    'table-primary--framed-full',
                    'table-primary__cell--header-tertiary',
                    'table-primary__cell--center'
                  )}
                />
                <th
                  className={classNames(
                    'table-primary--framed-top',
                    'table-primary--framed-bottom',
                    'table-primary__cell--header-tertiary',
                    'table-primary__cell--center'
                  )}>
                  {l('_EventDate')}
                </th>
                <th
                  className={classNames(
                    'table-primary--framed-top',
                    'table-primary--framed-bottom',
                    'table-primary__cell--header-tertiary',
                    'table-primary__cell--center'
                  )}>
                  {l('_EventName')}
                </th>
                <th
                  className={classNames(
                    'table-primary--framed-top',
                    'table-primary--framed-bottom',
                    'table-primary__cell--header-tertiary',
                    'table-primary__cell--center'
                  )}>
                  {l('_Amount')}
                </th>
                <th
                  className={classNames(
                    'table-primary--framed-top',
                    'table-primary--framed-bottom',
                    'table-primary__cell--header-tertiary',
                    'table-primary__cell--center'
                  )}>
                  {l('_CostOfEquity')}
                </th>
                <th
                  className={classNames(
                    'table-primary--framed-top',
                    'table-primary--framed-bottom',
                    'table-primary__cell--header-tertiary',
                    'table-primary__cell--center'
                  )}>
                  {l('_DiscountFactor')}
                </th>
                <th
                  className={classNames(
                    'table-primary--framed',
                    'table-primary--framed-top',
                    'table-primary--framed-bottom',
                    'table-primary__cell--header-tertiary',
                    'table-primary__cell--center'
                  )}>
                  {l('_PresentValue')}
                </th>
              </tr>
            </thead>
            <tbody
              className={classNames(
                'table-primary__table-section--framed-bottom',
                'table-primary--zebra'
              )}>
              {transactions.map((transaction, idx) => {
                const isDividendOrCashPay = isTransactionCashPaidOrDividend(transaction);
                return (
                  <tr key={idx}>
                    <td
                      className={classNames(
                        'table-primary--framed',
                        'table-primary__cell--vertical-separator-start'
                      )}>
                      <CellValue
                        value={getTransactionTypeLabel(transaction)}
                        alignment={Alignment.Center}
                      />
                    </td>
                    <td>
                      <CellValue
                        value={!isDividendOrCashPay ? transaction.date : undefined}
                        dateFormat={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                        alignment={Alignment.Center}
                        allowEmptyValues
                      />
                    </td>
                    <td>
                      <CellValue
                        value={!isDividendOrCashPay ? transaction.eventNarrative : undefined}
                        alignment={Alignment.Center}
                        allowEmptyValues
                      />
                    </td>
                    <td>
                      <CellValue
                        value={transaction.amount}
                        decimalPoint={0}
                        alignment={Alignment.Center}
                      />
                    </td>
                    <td>
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(transaction.costOfEquity)}
                        alignment={Alignment.Center}
                      />
                    </td>
                    <td>
                      <CellValue
                        value={transaction.discountFactor}
                        decimalPoint={2}
                        alignment={Alignment.Center}
                        normalize
                      />
                    </td>
                    <td className="table-primary--framed">
                      <CellValue
                        value={transaction.fairValue}
                        decimalPoint={0}
                        alignment={Alignment.Center}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} className="table-primary__cell--section-separator" />
              </tr>
              <tr>
                <td colSpan={5} />
                <td>
                  <CellValue value={totalPvValues?.title} alignment={Alignment.Center} strong />
                </td>
                <td>
                  <CellValue
                    value={totalPvValues?.value}
                    decimalPoint={0}
                    alignment={Alignment.Center}
                    strong
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </>
    </BaseModal>
  );
};
